'use client';

import { useCallback } from 'react';

export type EmailComposeRequest = {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  /** Optional File[] attachments (e.g. downloaded PDF blob wrapped as File). */
  attachments?: File[];
};

const STORAGE_KEY = 'email.composePending';

type SerializedRequest = {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: { name: string; type: string; data: string }[];
};

async function serialize(req: EmailComposeRequest): Promise<SerializedRequest> {
  const attachments = req.attachments && req.attachments.length > 0
    ? await Promise.all(req.attachments.map(async (file) => {
        const buf = await file.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = '';
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as unknown as number[]);
        }
        return { name: file.name, type: file.type, data: btoa(binary) };
      }))
    : undefined;
  return {
    to: req.to,
    cc: req.cc,
    bcc: req.bcc,
    subject: req.subject,
    bodyText: req.bodyText,
    bodyHtml: req.bodyHtml,
    attachments,
  };
}

function deserialize(data: SerializedRequest): EmailComposeRequest {
  const attachments = data.attachments?.map((att) => {
    const binary = atob(att.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], att.name, { type: att.type });
  });
  return {
    to: data.to,
    cc: data.cc,
    bcc: data.bcc,
    subject: data.subject,
    bodyText: data.bodyText,
    bodyHtml: data.bodyHtml,
    attachments,
  };
}

/**
 * Hook that returns a function to open the email compose window pre-filled
 * in a new browser tab. Uses sessionStorage to pass the request (including
 * File[] attachments serialized as base64) to the new tab.
 *
 * Usage:
 *   const composeEmail = useEmailCompose();
 *   composeEmail({ to: 'x@y.com', subject: 'Hello', bodyText: '...', bodyHtml: '<p>...</p>', attachments: [file] });
 */
export function useEmailCompose() {
  return useCallback(async (req: EmailComposeRequest) => {
    const serialized = await serialize(req);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
    window.open('/email?compose=pending', '_blank');
  }, []);
}

/** Internal — called by the email page on mount to consume and clear the request. */
export function consumeEmailComposeRequest(): EmailComposeRequest | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(STORAGE_KEY);
  try {
    return deserialize(JSON.parse(raw));
  } catch {
    return null;
  }
}
